import { useMutation } from '@apollo/client';
import * as React from 'react';
import ToastActions from 'actions/ToastActions';
import { saveReactionSummary, getReactionSummary } from './gqlCache';

import ADD_REACTION from './AddReaction.gql';
import type { AddReaction, AddReactionVariables } from './__generated__/AddReaction';

import REMOVE_REACTION from './RemoveReaction.gql';
import type { RemoveReaction, RemoveReactionVariables } from './__generated__/RemoveReaction';

import type { ReactionSummaryFragment } from './__generated__/ReactionSummaryFragment';

type InnerProps = {
  data: ReactionSummaryFragment | undefined;
  addReactionToSummary: (name: string) => void;
  removeReactionFromSummary: (name: string) => void;
};

export type Props = {
  token?: string;
  chatUuid: string;
  messageUuid: string;
  children: (props: {
    reactionSummary: ReactionSummaryFragment | undefined;
    addReactionToSummary: (name: string) => void;
    removeReactionFromSummary: (name: string) => void;
  }) => JSX.Element | null;
};

export function ReactionContainer(props: Props & InnerProps) {
  return props.children({
    reactionSummary: props.data,
    addReactionToSummary: props.addReactionToSummary,
    removeReactionFromSummary: props.removeReactionFromSummary,
  });
}

export default function ReactionContainerWrapper(props: Props) {
  const { messageUuid, chatUuid, token: chatMessageReactionToken } = props;

  const [addReaction] = useMutation<AddReaction, AddReactionVariables>(ADD_REACTION, {
    onError: (error) => {
      console.error(error);
    },
    update: (_proxy, { data }) => {
      const error = data?.addReaction.error;
      if (error) {
        ToastActions.showErrorToast('There was a problem saving your reaction');
        console.error('Error adding a reaction', error);
      }

      saveReactionSummary(data?.addReaction?.reactionSummary);
    },
  });

  const [removeReaction] = useMutation<RemoveReaction, RemoveReactionVariables>(REMOVE_REACTION, {
    update: (_proxy, { data }) => {
      const error = data?.removeReaction.error;
      if (error) {
        ToastActions.showErrorToast('There was a problem saving your reaction');
        console.error('Error adding a reaction', error);
      }
      saveReactionSummary(data?.removeReaction?.reactionSummary);
    },
  });

  const data = getReactionSummary(props.messageUuid);

  return (
    <ReactionContainer
      {...props}
      data={data || undefined}
      removeReactionFromSummary={(name: string) =>
        removeReaction({
          variables: { name, chatUuid, messageUuid, chatMessageReactionToken },
        })
      }
      addReactionToSummary={(name: string) =>
        addReaction({
          variables: { name, chatUuid, messageUuid, chatMessageReactionToken },
        })
      }
    />
  );
}
